// src/pages/Workforce/AttendanceGridView.jsx
import React from "react";
import { CheckCircle, Info, Clock, XCircle, PlaneTakeoff } from "lucide-react";
import { StatusCell, Legend } from "./AttendenceShared";

export default function AttendanceGridView({
  isAdmin, stats, daysInMonth, dayHeaders, year, month, gridData,
  myWorkforceId, empAgg, setModalCell, setModalEmpName, monthLabel
}) {
  const now = new Date();

  return (
    <div className="animate-in fade-in duration-200">
      {isAdmin && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 mb-3 sm:mb-4 shrink-0">
          {[
            { key: "present",  label: "Present",  color: "green",  icon: <CheckCircle  size={16} /> },
            { key: "late",     label: "Late",     color: "orange", icon: <Info         size={16} /> },
            { key: "half_day", label: "Half Day", color: "blue",   icon: <Clock        size={16} /> },
            { key: "absent",   label: "Absent",   color: "red",    icon: <XCircle      size={16} /> },
            { key: "on_leave", label: "On Leave", color: "purple", icon: <PlaneTakeoff size={16} /> },
          ].map(({ key, label, color, icon }) => (
            <div key={key} className={`bg-white dark:bg-[#161b22] p-2 sm:p-3 rounded-xl shadow-xs border border-${color}-200 dark:border-[#30363d] flex items-center gap-2 sm:gap-3`}>
              <div className={`p-1.5 sm:p-2 bg-${color}-50 dark:bg-${color}-950/40 text-${color}-600 dark:text-${color}-400 rounded-lg shrink-0`}>{icon}</div>
              <div>
                <p className={`text-[9px] sm:text-[10px] text-${color}-700 dark:text-${color}-400 font-bold uppercase`}>{label}</p>
                <p className={`text-lg sm:text-xl font-bold text-${color}-800 dark:text-white`}>{stats[key]}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Legend />

      <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-xs border border-gray-200 dark:border-[#30363d] overflow-hidden mb-8">
        <div className="overflow-x-auto w-full pb-4">
          <table className="border-collapse w-full" style={{ minWidth: `${200 + daysInMonth * 38 + 160}px` }}>
            <thead className="bg-white dark:bg-[#161b22]">
              <tr className="border-b border-gray-200 dark:border-[#30363d]">
                <th className="sticky left-0 z-30 bg-gray-50 dark:bg-[#21262d] border-r border-gray-200 dark:border-[#30363d] px-3 sm:px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap" style={{ minWidth: 180 }}>Employee</th>
                {dayHeaders.map(({ day, dayName }) => {
                  const isToday = (year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate());
                  const isWeekend = dayName === "Sun" || dayName === "Sat";
                  return (
                    <th key={day} className={`w-9 min-w-[36px] px-0 py-2 text-center ${isWeekend ? "bg-gray-50 dark:bg-[#21262d]" : "bg-white dark:bg-[#161b22]"}`}>
                      <div className={`text-xs font-bold leading-none ${isToday ? "text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-slate-300"}`}>{day}</div>
                      <div className={`text-[9px] font-medium leading-none mt-1 ${isToday ? "text-blue-400 dark:text-blue-400" : "text-gray-400 dark:text-slate-500"}`}>{dayName}</div>
                    </th>
                  );
                })}
                {[
                  { h: "P",  cls: "text-green-600 dark:text-emerald-400"  }, { h: "L",  cls: "text-orange-500 dark:text-amber-400" }, { h: "HD", cls: "text-blue-500 dark:text-blue-400"   },
                  { h: "A",  cls: "text-red-500 dark:text-rose-400"    }, { h: "OL", cls: "text-purple-500 dark:text-purple-400" },
                ].map(({ h, cls }) => <th key={h} className={`hidden md:table-cell w-9 px-0 py-2 text-center text-[10px] font-bold bg-gray-50 dark:bg-[#21262d] ${cls}`}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {gridData.length === 0 ? (
                <tr><td colSpan={daysInMonth + 6} className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">No attendance data found for {monthLabel} {year}.</td></tr>
              ) : gridData.map((emp, idx) => {
                const agg = empAgg(emp.days || []);
                const isMe = String(emp.id) === String(myWorkforceId);
                const initials = emp.name ? emp.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "??";
                return (
                  <tr key={emp.id} className={`transition ${idx % 2 === 0 ? "bg-white dark:bg-[#161b22]" : "bg-gray-50/50 dark:bg-[#21262d]/50"} hover:bg-blue-50/30 dark:hover:bg-slate-800/40`}>
                    <td className={`sticky left-0 z-10 px-2 sm:px-3 py-2 border-r border-gray-100 dark:border-[#30363d] ${idx % 2 === 0 ? "bg-white dark:bg-[#161b22]" : "bg-gray-50 dark:bg-[#21262d]"} ${isMe ? "bg-blue-50/60 dark:bg-blue-950/40" : ""}`} style={{ minWidth: 180 }}>
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isMe ? "bg-blue-600 text-white" : "bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-200"}`}>{initials}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5"><p className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-white truncate leading-tight">{emp.name}</p>{isMe && <span className="shrink-0 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-[9px] font-bold rounded-full border border-blue-200 dark:border-blue-800/60">You</span>}</div>
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 truncate leading-tight">{emp.role || emp.employee_id}</p>
                        </div>
                      </div>
                    </td>
                    {(emp.days || []).map(dayObj => <StatusCell key={dayObj.day} day={dayObj} onClick={(d) => { setModalCell(d); setModalEmpName(emp.name); }} />)}
                    <td className="hidden md:table-cell w-9 px-0 text-center text-xs font-bold text-green-700 dark:text-emerald-400">{agg.P  || "—"}</td>
                    <td className="hidden md:table-cell w-9 px-0 text-center text-xs font-bold text-orange-500 dark:text-amber-400">{agg.L  || "—"}</td>
                    <td className="hidden md:table-cell w-9 px-0 text-center text-xs font-bold text-blue-500 dark:text-blue-400">{agg.HD || "—"}</td>
                    <td className="hidden md:table-cell w-9 px-0 text-center text-xs font-bold text-red-500 dark:text-rose-400">{agg.A  || "—"}</td>
                    <td className="hidden md:table-cell w-9 px-0 text-center text-xs font-bold text-purple-500 dark:text-purple-400">{agg.OL || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}